import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import Sidebar from '@/components/Sidebar';
import ChatInput from '@/components/ChatInput';
import ChatMessage from '@/components/ChatMessage';
import ArtifactsPanel from '@/components/ArtifactsPanel';
import OnboardingModal from '@/components/OnboardingModal';
import TemplatesPanel from '@/components/TemplatesPanel';
import { motion, AnimatePresence } from 'framer-motion';
import { List, Plus, Sparkle, Command, BookmarkSimple, Export, ShareNetwork, Copy, Check } from '@phosphor-icons/react';
import ProcessingVisual from '@/components/ProcessingVisual';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { routeQuery } from '@/lib/orchestrator';
import { getLocalEngine, streamLocal, interruptLocal, getSavedTier, saveTier, LOCAL_MODELS, runHeavy, probeWebGPU, getCachedModels, getLastLocalError, hasExplicitTier, recommendTier } from '@/lib/localEngine';
import HeavyTransition from '@/components/HeavyTransition';
import HeavyOrchestraPanel from '@/components/HeavyOrchestraPanel';
import ModelManager from '@/components/ModelManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOCAL_SYSTEM = "You are Lindey, a warm, friendly and capable AI assistant running fully on the user's own device for privacy. Be helpful and concise. Use markdown formatting when useful. Never mention which underlying model you are — you are simply Lindey.";

export default function ChatPage() {
  const { user, logout } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentMode, setAgentMode] = useState(false);
  const [chatMode, setChatMode] = useState('standard'); // 'standard' or 'thinking'
  const [localTier, setLocalTier] = useState(getSavedTier());
  const [modelLoad, setModelLoad] = useState(null); // { progress, text, tier }
  const [localGenId, setLocalGenId] = useState(null);
  const [heavyMode, setHeavyMode] = useState(() => { try { return localStorage.getItem('lindey_heavy') === '1'; } catch { return false; } });
  const [showHeavyTransition, setShowHeavyTransition] = useState(false);
  const [heavyPhase, setHeavyPhase] = useState(null);
  const [gpuReady, setGpuReady] = useState(null);
  const [showModelManager, setShowModelManager] = useState(false);
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const switchRef = useRef(false);
  const pendingLocalRef = useRef(null);
  const cloudNoticeRef = useRef(false);
  const [credits, setCredits] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const [artifact, setArtifact] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showDataMigration, setShowDataMigration] = useState(false);
  const streamingRef = useRef(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);

  const scroll = useCallback(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), []);
  useEffect(() => { scroll(); }, [messages, scroll]);

  // Show data migration notice for new users
  useEffect(() => {
    try {
      if (localStorage.getItem('lindey_new_user') === '1') {
        setShowDataMigration(true);
        localStorage.removeItem('lindey_new_user');
      }
    } catch { /* */ }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); searchRef.current?.focus(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') { e.preventDefault(); newChat(); setTimeout(() => inputRef.current?.focus(), 100); }
      if (e.key === 'Escape') { if (artifact) setArtifact(null); else inputRef.current?.focus(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [artifact]);

  const loadConvos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/conversations`);
      setConversations(data);
      // Show onboarding if no conversations and not dismissed
      if (data.length === 0 && !sessionStorage.getItem('lindey-onboarded')) {
        setShowOnboarding(true);
      }
    }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  // Load credits
  const loadCredits = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/user/credits`);
      setCredits(data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { loadCredits(); }, [loadCredits]);

  // Probe the real GPU adapter once — navigator.gpu existing is NOT enough
  useEffect(() => {
    probeWebGPU().then(g => {
      setGpuReady(g.supported);
      // Scheduler: unless the user picked a tier, use the best one that fits this device
      if (g.supported && !hasExplicitTier()) setLocalTier(recommendTier(g));
    }).catch(() => setGpuReady(false));
  }, []);

  const cloudFallbackNotice = () => {
    if (cloudNoticeRef.current) return;
    cloudNoticeRef.current = true;
    setCreditToast('On-device AI unavailable on this device — using stateless Private Cloud instead. Open "Manage models" for details.');
    setTimeout(() => setCreditToast(null), 7000);
  };

  // Lindey Heavy mode
  useEffect(() => {
    document.documentElement.classList.toggle('heavy', heavyMode);
    return () => document.documentElement.classList.remove('heavy');
  }, [heavyMode]);

  const toggleHeavy = () => {
    if (heavyMode) {
      setHeavyMode(false);
      try { localStorage.removeItem('lindey_heavy'); } catch { /* */ }
    } else {
      setShowHeavyTransition(true);
    }
  };

  const onHeavyTransitionDone = async () => {
    setShowHeavyTransition(false);
    setHeavyMode(true);
    try { localStorage.setItem('lindey_heavy', '1'); } catch { /* */ }
    // Provision models up-front so Heavy runs are instant
    try {
      const gpu = await probeWebGPU();
      if (!gpu.supported) { cloudFallbackNotice(); return; }
      const cached = await getCachedModels();
      if (Object.values(cached).some(Boolean)) {
        getLocalEngine(localTier, (p) => setModelLoad(p)).then(() => setModelLoad(null)).catch(() => setModelLoad(null));
      } else {
        setShowModelManager(true);
      }
    } catch { /* */ }
  };

  useEffect(() => {
    if (!activeConvo) { setMessages([]); return; }
    if (streamingRef.current) return;
    (async () => {
      try { const { data } = await axios.get(`${API}/conversations/${activeConvo}/messages`); setMessages(data); }
      catch (e) { console.error(e); }
    })();
  }, [activeConvo]);

  const newChat = () => { setActiveConvo(null); setMessages([]); setArtifact(null); };
  const selectConvo = (id) => { setActiveConvo(id); setMobileMenu(false); setArtifact(null); };
  const deleteConvo = async (id) => {
    try {
      await axios.delete(`${API}/conversations/${id}`);
      if (activeConvo === id) { setActiveConvo(null); setMessages([]); }
      loadConvos();
    } catch (e) { console.error(e); }
  };

  const renameConvo = async (id, title) => {
    try {
      await axios.patch(`${API}/conversations/${id}`, { title });
      loadConvos();
    } catch (e) { console.error(e); }
  };

  const pinConvo = async (id) => {
    try {
      await axios.patch(`${API}/conversations/${id}/pin`, {});
      loadConvos();
    } catch (e) { console.error(e); }
  };

  // Edit user message
  const editMessage = async (msgId, newContent) => {
    try {
      await axios.put(`${API}/messages/${msgId}`, { content: newContent });
      // Reload messages (edit deletes subsequent messages)
      const { data } = await axios.get(`${API}/conversations/${activeConvo}/messages`);
      setMessages(data);
      // Resend with new content via streaming
      await sendMessage(newContent);
    } catch (e) { console.error(e); }
  };

  // Regenerate AI response
  const regenerateMessage = async (msgId) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/messages/${msgId}/regenerate`, {});
      setMessages(p => p.map(m => m.id === msgId ? data : m));
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  // System Orchestrator: route each request to on-device or Private Cloud
  const sendMessage = async (content, fileContext = null) => {
    if (!content.trim()) return;
    if (heavyMode && !agentMode && !fileContext) {
      if (gpuReady === false) { cloudFallbackNotice(); return sendCloudStream(content); }
      return sendLocal(content, true);
    }
    const decision = routeQuery(content, { agentMode, hasFile: !!fileContext, mode: chatMode, gpuReady });
    if (decision.route === 'local') return sendLocal(content);
    if (decision.reason === 'no-webgpu') cloudFallbackNotice();
    return sendCloudStream(content, fileContext);
  };

  // On-device processing via WebLLM (WebGPU). heavy=true runs the Lindey Heavy 60-shard orchestration
  const sendLocal = async (content, heavy = false) => {
    const tid = `t-${Date.now()}`;
    const placeholderId = `local-${Date.now()}`;
    const history = messages
      .filter(m => (m.role === 'user' || m.role === 'assistant') && m.content)
      .slice(-8)
      .map(m => ({ role: m.role, content: m.content }));
    setMessages(p => [...p, { id: tid, role: 'user', content, created_at: new Date().toISOString() }]);
    setLoading(true);
    switchRef.current = false;
    pendingLocalRef.current = { content, tid, placeholderId };
    setLocalGenId(placeholderId);
    try {
      const { engine, tierUsed } = await getLocalEngine(localTier, (p) => setModelLoad(p));
      setModelLoad(null);
      if (switchRef.current) return;
      let label = heavy ? 'Lindey Heavy' : LOCAL_MODELS[tierUsed].label;
      setMessages(p => [...p, { id: placeholderId, role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true, processed_locally: true, local_model: label }]);
      let streamed = '';
      if (heavy) {
        const result = await runHeavy(engine, [{ role: 'system', content: LOCAL_SYSTEM }, ...history], content, {
          onPhase: (ph) => setHeavyPhase(ph),
          onStream: (text) => {
            setMessages(p => p.map(m => m.id === placeholderId ? { ...m, content: text } : m));
            scroll();
          },
          isCancelled: () => switchRef.current,
        });
        streamed = result.text;
        label = `Lindey Heavy · ${result.shards} shards`;
        setHeavyPhase(null);
        setMessages(p => p.map(m => m.id === placeholderId ? { ...m, local_model: label } : m));
      } else {
        streamed = await streamLocal(engine, [
          { role: 'system', content: LOCAL_SYSTEM },
          ...history,
          { role: 'user', content }
        ], (text) => {
          setMessages(p => p.map(m => m.id === placeholderId ? { ...m, content: text } : m));
          scroll();
        });
      }
      if (switchRef.current) return;
      setMessages(p => p.map(m => m.id === placeholderId ? { ...m, streaming: false } : m));
      try {
        const { data } = await axios.post(`${API}/chat/local`, {
          conversation_id: activeConvo, user_content: content, assistant_content: streamed, local_model: label
        });
        setMessages(p => p.map(m =>
          m.id === tid ? { ...m, id: data.user_message.id } :
          m.id === placeholderId ? { ...m, id: data.assistant_message.id } : m
        ));
        if (!activeConvo) setActiveConvo(data.conversation_id);
        loadConvos();
      } catch (persistErr) { console.error('Persist failed', persistErr); }
    } catch (e) {
      if (switchRef.current) return;
      console.error('Local inference failed', e);
      setMessages(p => p.filter(m => m.id !== tid && m.id !== placeholderId));
      setLoading(false); setLocalGenId(null); setModelLoad(null); setHeavyPhase(null); pendingLocalRef.current = null;
      const reason = getLastLocalError();
      setCreditToast(reason
        ? `On-device model unavailable (${String(reason).slice(0, 90)}) — using Private Cloud for this one.`
        : 'On-device model unavailable — using Private Cloud for this one.');
      setTimeout(() => setCreditToast(null), 7000);
      await sendCloudStream(content);
      return;
    }
    setLoading(false); setLocalGenId(null); setModelLoad(null); setHeavyPhase(null); pendingLocalRef.current = null;
  };

  // "Switch to web": abort local generation and re-route to Private Cloud
  const switchToWeb = () => {
    const pending = pendingLocalRef.current;
    if (!pending) return;
    switchRef.current = true;
    interruptLocal();
    pendingLocalRef.current = null;
    setMessages(p => p.filter(m => m.id !== pending.tid && m.id !== pending.placeholderId));
    setLocalGenId(null); setModelLoad(null); setHeavyPhase(null);
    sendCloudStream(pending.content);
  };

  // Streaming message sender (Private Cloud Compute)
  const sendCloudStream = async (content, fileContext = null) => {
    if (!content.trim()) return;
    const tid = `t-${Date.now()}`;
    setMessages(p => [...p, { id: tid, role: 'user', content, created_at: new Date().toISOString() }]);
    setLoading(true);
    const placeholderId = `stream-${Date.now()}`;

    try {
      const token = localStorage.getItem('lindey_access_token') || '';
      const response = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ content, conversation_id: activeConvo, agent_mode: agentMode, file_context: fileContext, mode: chatMode })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      streamingRef.current = true;
      setStreamingMsgId(placeholderId);
      setMessages(p => {
        const withUser = p.map(m => m.id === tid ? { ...m } : m);
        return [...withUser, { id: placeholderId, role: 'assistant', content: '', agent_steps: null, created_at: new Date().toISOString(), streaming: true }];
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = '';
      let convId = activeConvo;
      let aiMsgId = null;
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6).trim());
            if (evt.type === 'meta') {
              aiMsgId = evt.ai_msg_id; convId = evt.conversation_id;
              if (!activeConvo) setActiveConvo(convId);
              if (evt.mode_switched) {
                setChatMode('standard');
              }
            }
            else if (evt.type === 'agent_steps') { setMessages(p => p.map(m => m.id === placeholderId ? { ...m, agent_steps: evt.steps } : m)); }
            else if (evt.type === 'agent_step') { setMessages(p => p.map(m => m.id === placeholderId ? { ...m, agent_steps: evt.steps } : m)); scroll(); }
            else if (evt.type === 'chunk') { streamedText += evt.text; const t = streamedText; setMessages(p => p.map(m => m.id === placeholderId ? { ...m, content: t } : m)); scroll(); }
            else if (evt.type === 'done') { setMessages(p => p.map(m => m.id === placeholderId ? { ...m, id: aiMsgId || placeholderId, content: streamedText, streaming: false } : m)); setStreamingMsgId(null); }
          } catch (_) {}
        }
      }
      if (streamedText) setMessages(p => p.map(m => m.id === placeholderId ? { ...m, id: aiMsgId || placeholderId, content: streamedText, streaming: false } : m));
      streamingRef.current = false;
      setStreamingMsgId(null);
      loadConvos();
      loadCredits();
    } catch (e) {
      console.error('Stream error', e);
      streamingRef.current = false;
      try {
        const { data } = await axios.post(`${API}/chat`, { content, conversation_id: activeConvo, agent_mode: agentMode });
        if (!activeConvo) setActiveConvo(data.conversation_id);
        setMessages(p => [...p.filter(m => m.id !== tid && m.id !== placeholderId), data.user_message, data.assistant_message]);
        loadConvos();
      } catch (e2) {
        setMessages(p => [...p.filter(m => m.id !== placeholderId), { id: `e-${Date.now()}`, role: 'assistant', content: 'Something went wrong. Please try again.', created_at: new Date().toISOString() }]);
      }
      setStreamingMsgId(null);
    } finally { setLoading(false); }
  };

  const genImage = async (prompt) => {
    const tid = `t-${Date.now()}`;
    setMessages(p => [...p, { id: tid, role: 'user', content: `Generate image: ${prompt}`, created_at: new Date().toISOString() }]);
    setLoading(true);
    setImageGenLoading(true);
    try {
      const { data } = await axios.post(`${API}/generate-image`, { prompt, conversation_id: activeConvo });
      if (!activeConvo) setActiveConvo(data.conversation_id);
      setMessages(p => [...p.filter(m => m.id !== tid), data.user_message, data.assistant_message]);
      loadConvos();
    } catch (e) {
      setMessages(p => [...p, { id: `e-${Date.now()}`, role: 'assistant', content: 'Image generation failed.', created_at: new Date().toISOString() }]);
    } finally { setLoading(false); setImageGenLoading(false); }
  };

  const shareConvo = async () => {
    if (!activeConvo) return;
    try {
      const { data } = await axios.post(`${API}/conversations/${activeConvo}/share`);
      setShareCopied(false);
      setShareInfo({ url: `${window.location.origin}/share/${data.share_id}` });
    } catch (e) { console.error(e); }
  };

  const revokeShare = async () => {
    try { await axios.delete(`${API}/conversations/${activeConvo}/share`); } catch { /* */ }
    setShareInfo(null);
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    setLoading(true);
    try {
      const { data: fileData } = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const extracted = fileData.extracted_text || '';
      await sendMessage(`I uploaded a file: **${file.name}** (${(file.size / 1024).toFixed(1)} KB). Please analyze its contents.`, extracted || null);
    } catch (e) { console.error(e); setLoading(false); }
  };

  const sendMultimodal = async (text, imageFile) => {
    const fd = new FormData();
    fd.append('content', text);
    fd.append('file', imageFile);
    if (activeConvo) fd.append('conversation_id', activeConvo);

    const tid = `t-${Date.now()}`;
    const imgPreview = URL.createObjectURL(imageFile);
    setMessages(p => [...p, {
      id: tid, role: 'user', content: text,
      image_preview: imgPreview,
      created_at: new Date().toISOString()
    }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/chat/multimodal`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!activeConvo) setActiveConvo(data.conversation_id);
      setMessages(p => {
        const filtered = p.filter(m => m.id !== tid);
        return [...filtered, data.user_message, data.assistant_message];
      });
      loadConvos();
    } catch (e) {
      console.error(e);
      setMessages(p => [...p, { id: `e-${Date.now()}`, role: 'assistant', content: 'Failed to process image. Please try again.', created_at: new Date().toISOString() }]);
    } finally {
      setLoading(false);
      URL.revokeObjectURL(imgPreview);
    }
  };

  const sidebar = (
    <Sidebar conversations={conversations} activeConvo={activeConvo} onSelect={selectConvo} onDelete={deleteConvo} onNew={newChat} onRename={renameConvo} onPin={pinConvo} user={user} onLogout={logout} searchRef={searchRef} />
  );

  const exportConvo = async () => {
    if (!activeConvo) return;
    try {
      const response = await axios.get(`${API}/conversations/${activeConvo}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'conversation.md';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    sessionStorage.setItem('lindey-onboarded', '1');
  };

  const useTemplate = (prompt) => {
    setShowTemplates(false);
    // Set input message via a callback
    setTemplateText(prompt);
  };

  const [templateText, setTemplateText] = useState('');

  const buyCredits = async (amount = 1, cashappName = '') => {
    try {
      const { data } = await axios.post(`${API}/user/credits/buy`, { amount, cashapp_name: cashappName });
      setCreditToast(data.message || 'Purchase submitted!');
      setTimeout(() => setCreditToast(null), 4000);
    } catch (e) {
      setCreditToast(e.response?.data?.detail || 'Purchase failed');
      setTimeout(() => setCreditToast(null), 3000);
    }
  };

  const [creditToast, setCreditToast] = useState(null);

  return (
    <div className="h-screen flex bg-transparent" data-testid="chat-page">
      <aside className="hidden md:flex w-[260px] shrink-0 flex-col border-r border-white/10 bg-white/[0.03]">
        {sidebar}
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-12 shrink-0 flex items-center justify-between px-4 border-b border-white/10 glass-header">
          <div className="flex items-center gap-2">
            <Sheet open={mobileMenu} onOpenChange={setMobileMenu}>
              <SheetTrigger asChild>
                <button className="md:hidden w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:bg-white/10" data-testid="mobile-menu-button">
                  <List className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[270px] bg-white/[0.03] border-white/10">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SheetDescription className="sr-only">Chat navigation sidebar</SheetDescription>
                {sidebar}
              </SheetContent>
            </Sheet>
            <button onClick={() => { newChat(); setTimeout(() => inputRef.current?.focus(), 100); }} className="flex items-center gap-1.5 text-xs font-medium text-white/50 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="new-chat-button">
              <Plus className="w-3.5 h-3.5" weight="bold" /> New chat
            </button>
            <button onClick={() => setShowTemplates(true)} className="flex items-center gap-1.5 text-xs font-medium text-white/50 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="templates-button">
              <BookmarkSimple className="w-3.5 h-3.5" /> Templates
            </button>
            {activeConvo && (
              <>
                <button onClick={shareConvo} className="flex items-center gap-1.5 text-xs font-medium text-white/50 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="share-button">
                  <ShareNetwork className="w-3.5 h-3.5" /> Share
                </button>
                <button onClick={exportConvo} className="flex items-center gap-1.5 text-xs font-medium text-white/50 hover:text-white px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="export-button">
                  <Export className="w-3.5 h-3.5" /> Export
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-white/40">
              <kbd className="px-1.5 py-0.5 rounded border border-white/10 bg-white/5 font-mono"><Command className="w-2.5 h-2.5 inline" />K</kbd> Search
              <kbd className="px-1.5 py-0.5 rounded border border-white/10 bg-white/5 font-mono ml-2"><Command className="w-2.5 h-2.5 inline" />N</kbd> New
            </div>
            <div className="flex items-center gap-1 text-xs text-white/40">
              {heavyMode ? (
                <span className="flex items-center gap-1 font-bold heavy-gradient-text" data-testid="header-heavy-indicator">
                  <Sparkle weight="fill" className="w-3 h-3 text-lime-400" /> Lindey Heavy
                </span>
              ) : (
                <><Sparkle weight="fill" className="w-3 h-3 text-lime-500" /> Lindey</>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Chat area */}
          <div className={`flex-1 flex flex-col min-w-0 ${artifact ? 'w-1/2' : ''}`}>
            <div className="flex-1 overflow-y-auto" data-testid="chat-messages-area">
              <div className="max-w-3xl mx-auto w-full px-4 py-6">
                {messages.length === 0 ? <EmptyState onSuggest={sendMessage} /> : (
                  <AnimatePresence initial={false}>
                    {messages.map((m, i) => (
                      <ChatMessage
                        key={m.id || i}
                        message={m}
                        onEdit={editMessage}
                        onRegenerate={regenerateMessage}
                        onArtifact={setArtifact}
                      />
                    ))}
                  </AnimatePresence>
                )}
                {loading && !streamingMsgId && !localGenId && (
                  <ProcessingVisual variant={imageGenLoading ? 'image' : agentMode ? 'agent' : chatMode === 'thinking' ? 'thinking' : 'cloud'} />
                )}
                {heavyPhase && <HeavyOrchestraPanel phase={heavyPhase} />}
                {localGenId && (
                  <div className="flex justify-start pl-9 mb-4">
                    <button onClick={switchToWeb} data-testid="switch-to-web-button"
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium text-white/50 hover:text-lime-300 px-3 py-1.5 rounded-full border border-white/10 hover:border-lime-400/40 hover:bg-lime-400/10 transition-all">
                      Taking too long? Switch to web →
                    </button>
                  </div>
                )}
                <div ref={endRef} />
              </div>
            </div>
            <div className="shrink-0 px-4 pb-4 pt-2 bg-transparent">
              <div className="max-w-3xl mx-auto">
                {modelLoad && (
                  <div className="mb-2 rounded-xl border border-lime-400/25 bg-lime-400/10 px-4 py-3" data-testid="model-load-progress">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-semibold text-lime-300">
                        Preparing {LOCAL_MODELS[modelLoad.tier]?.label || 'on-device model'} — first load can take a few minutes
                      </span>
                      <span className="text-[11px] font-mono text-lime-300">{Math.round((modelLoad.progress || 0) * 100)}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-lime-400/15 overflow-hidden">
                      <div className="h-full energy-bar rounded-full transition-all duration-300" style={{ width: `${Math.max(3, (modelLoad.progress || 0) * 100)}%` }} />
                    </div>
                    <p className="text-[10px] text-lime-300/70 mt-1.5 truncate">{modelLoad.text}</p>
                  </div>
                )}
                <ChatInput onSend={sendMessage} onImage={genImage} onFile={uploadFile} onMultimodal={sendMultimodal} agentMode={agentMode} onToggleAgent={() => setAgentMode(!agentMode)} loading={loading} inputRef={inputRef} templateText={templateText} onTemplateUsed={() => setTemplateText('')} mode={chatMode} onToggleMode={() => setChatMode(m => m === 'standard' ? 'thinking' : 'standard')} credits={credits} onBuyCredits={buyCredits} localTier={localTier} onTierChange={(t) => { saveTier(t); setLocalTier(t); }} heavyMode={heavyMode} onToggleHeavy={toggleHeavy} onManageModels={() => setShowModelManager(true)} />
              </div>
            </div>
          </div>

          {/* Artifacts Panel */}
          {artifact && <ArtifactsPanel artifact={artifact} onClose={() => setArtifact(null)} />}
        </div>
      </main>

      {/* Heavy mode transition */}
      {showHeavyTransition && <HeavyTransition onComplete={onHeavyTransitionDone} />}

      {/* On-device model manager */}
      <ModelManager open={showModelManager} onOpenChange={setShowModelManager} />

      {/* Share modal */}
      <Dialog open={!!shareInfo} onOpenChange={(o) => !o && setShareInfo(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="share-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Clash Display, sans-serif' }}>Share this conversation</DialogTitle>
            <DialogDescription className="text-sm text-white/50 pt-1">
              Anyone with this link can read the conversation. Revoke it anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <input readOnly value={shareInfo?.url || ''} data-testid="share-url-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white/80 truncate" />
            <button onClick={() => { try { navigator.clipboard.writeText(shareInfo.url); } catch { /* */ } setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }}
              data-testid="share-copy-button"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-lime-500 hover:bg-lime-600 text-black text-xs font-semibold transition-colors shrink-0">
              {shareCopied ? <Check weight="bold" className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {shareCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button onClick={revokeShare} data-testid="share-revoke-button"
            className="text-xs text-red-400 hover:text-red-300 transition-colors self-start">
            Revoke share link
          </button>
        </DialogContent>
      </Dialog>

      {/* Onboarding Modal */}
      {showOnboarding && <OnboardingModal onDismiss={dismissOnboarding} />}

      {/* Templates Panel */}
      <TemplatesPanel open={showTemplates} onClose={() => setShowTemplates(false)} onUse={useTemplate} />

      {/* Credit Purchase Toast */}
      <AnimatePresence>
        {creditToast && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium shadow-xl" data-testid="credit-toast">
            {creditToast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Data Migration Notice for New Users */}
      <Dialog open={showDataMigration} onOpenChange={setShowDataMigration}>
        <DialogContent className="sm:max-w-[400px]" data-testid="data-migration-modal">
          <DialogHeader>
            <div className="w-10 h-10 rounded-xl bg-lime-400/100 flex items-center justify-center mb-2">
              <Sparkle weight="fill" className="w-5 h-5 text-white" />
            </div>
            <DialogTitle className="text-lg font-semibold" style={{ fontFamily: 'Clash Display, sans-serif' }}>Welcome to Lindey 10</DialogTitle>
            <DialogDescription className="text-sm text-white/50 pt-1">Your data is on its way. This could take a few days.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-white/50 leading-relaxed">We're importing your previous conversations and data into your new account. You'll be notified once everything is ready. In the meantime, feel free to start chatting!</p>
          <button onClick={() => setShowDataMigration(false)} data-testid="dismiss-migration-notice"
            className="w-full mt-2 py-2.5 rounded-xl bg-lime-400/100 hover:bg-lime-600 text-white font-semibold text-sm transition-colors">
            Got it
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EmptyState({ onSuggest }) {
  const items = [
    "Write a Python web scraper with error handling",
    "Explain transformers in machine learning",
    "Solve: integrate x^2 * e^x dx step by step",
    "Generate an image of a neon city at night",
  ];
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="empty-chat-state">
      <div className="w-12 h-12 rounded-xl bg-lime-400/100 flex items-center justify-center mb-5 shadow-lg shadow-lime-500/15">
        <Sparkle weight="fill" className="w-6 h-6 text-white" />
      </div>
      <h2 className="text-xl font-semibold tracking-tight text-white mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>What can I help with?</h2>
      <p className="text-sm text-white/40 mb-8">Code, math, images, research — just ask.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-lg">
        {items.map((s, i) => (
          <motion.button key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.06 }}
            onClick={() => onSuggest(s)}
            className="text-left px-4 py-3 rounded-lg border border-white/10 hover:border-lime-400/25 hover:bg-lime-400/10 transition-all text-[13px] text-white/50 hover:text-white/90"
            data-testid={`suggestion-${i}`}
          >{s}</motion.button>
        ))}
      </div>
    </motion.div>
  );
}
